import {
  FunctionComponent,
  ReactElement,
  useCallback,
  useMemo,
  useState,
} from 'react'
import { useTokenAmil } from '../../store/useTokenAmil'
import { returnFormattedDate } from '@/utils/fnUtils'
import Icon from '@/components/Iconography'
import { getTheme, Input, Text } from '@conexasaude/conexa-hero'
import Modal from '@/components/Modal'
import useStyles from './styles'
import { errorMessages } from './contants'
import { useModalContext } from '@/components/Modal/contexts/ModalContext'
import { useAttendanceCallStore } from '../../store/useAttendanceCall'
import { useForm } from 'react-hook-form'
import { ExternalAuthorizationToken, IconType } from './types'
import { sanitizeAlphaNumeric } from '@/utils/string'
import { zodResolver } from '@hookform/resolvers/zod'
import { sendCallTokenSchema } from '../../validations/sendCallToken.validation'
import { useValidateCallToken } from '../../queries/useValidateCallToken'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { getToken } from '@/utils/token'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'

const CallStartedModalWithToken: FunctionComponent<{
  onActionClick: (callback: () => void) => void
}> = ({ onActionClick }) => {
  const { clinic } = useAuthContext()
  const styles = useStyles()
  const theme = getTheme()
  const [validated, setValidated] = useState<boolean>()
  const [errorMessage, setErrorMessage] = useState<string>(null)
  const guideNumber = useTokenAmil(state => state.guideNumber)

  const attendanceCall = useAttendanceCallStore(store => store.attendanceCall)
  const { closeModal } = useModalContext()
  const { mutateAsync: validateCallToken, isLoading } = useValidateCallToken()
  const { logAnalyticsEvent } = useFirebaseContext()

  const form = useForm<ExternalAuthorizationToken>({
    resolver: zodResolver(sendCallTokenSchema),
    mode: 'onSubmit',
    defaultValues: {
      externalAuthorizationToken: '',
    },
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = form

  const checkToken = async data => {
    try {
      await validateCallToken({
        attendanceId: attendanceCall?.attendanceId,
        professionalId: attendanceCall?.professionalId,
        date: returnFormattedDate(),
        externalAuthorizationToken: data?.externalAuthorizationToken,
        providerGuideNumber: guideNumber,
      })
      setValidated(true)
      closeModal()
      onActionClick(resetToken)
    } catch (error) {
      setValidated(false)
      setErrorMessage(
        errorMessages('errorInValidation', error?.response?.data?.msg),
      )
    }
  }

  const resetToken = useCallback(() => {
    reset()
    setValidated(false)
  }, [reset])

  const handleAlphaNumericInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const input = event.target
    const sanitizedValue = sanitizeAlphaNumeric(input?.value)
    setValue('externalAuthorizationToken', sanitizedValue, {
      shouldValidate: true,
    })
  }

  const chooseIcon = (icon: IconType): ReactElement => {
    const icons = {
      loading: <Icon name="loading" color={theme.colors.neutral[300]} />,
      validated: (
        <Icon name="check" color={theme.colors.feedback.success[300]} />
      ),
      none: null,
    }
    return icons[icon]
  }

  const getIconType = useMemo(() => {
    if (isLoading) return 'loading'
    if (validated) return 'validated'
    return 'none'
  }, [isLoading, validated])

  const handleMountNegativeModalAction = useMemo(() => {
    if (clinic?.urlHelpToken) {
      try {
        const token = getToken()
        const url = new URL(clinic?.urlHelpToken)
        url.searchParams.set('token', token)
        const urlWithParams = url.toString()

        return {
          negativeActionLabel: 'Não encontrei o token',
          negativeAction: () => {
            logAnalyticsEvent('Pac_Clique_Help_Token')
            window.open(urlWithParams, '_blank')
          },
        }
      } catch (error) {
        console.error('Error creating URL with token:', error)
      }
    }
  }, [clinic?.urlHelpToken, logAnalyticsEvent])

  return (
    <form onSubmit={handleSubmit(checkToken)}>
      <Modal
        primaryAction={handleSubmit(checkToken)}
        busyPrimaryAction={isLoading}
        primaryActionLabel="Iniciar consulta"
        id="CALL_STARTED_WITH_TOKEN"
        size={520}
        {...handleMountNegativeModalAction}
      >
        <div css={styles.container}>
          <Text variant="paragraphLarge">
            Para iniciar sua consulta, insira o token de segurança. O token pode
            ter sido enviado para você por SMS, e-mail ou estar disponível no
            aplicativo do seu plano de saúde.
          </Text>
          <div css={styles.inputContainer}>
            <Input
              {...register('externalAuthorizationToken')}
              onChange={handleAlphaNumericInputChange}
              label="Token de segurança"
              placeholder="Digite o código aqui"
              error={
                errorMessage || errors?.externalAuthorizationToken?.message
              }
              disabled={isLoading}
              icon={chooseIcon(getIconType)}
              fullWidth
            />
          </div>
        </div>
      </Modal>
    </form>
  )
}

export default CallStartedModalWithToken
